/**
 * PaymentTypeFields — bloco de tipo de pagamento e seus campos dependentes,
 * compartilhado pela baixa/liquidação (settlement) e pelo Registrar Pagamento (billing).
 *
 * Comportamento (o mesmo nas duas telas):
 *  - FINANCIAL       → Meio de Pagamento + Conta de Recebimento
 *  - DEBT_CONFESSION → Contrato de Confissão de Dívida (opcional)
 *  - ASSET_DATION    → lista de bens (AssetDationForm)
 * Em todos os tipos: Fonte do Pagamento e Aval a devolver — e SÓ onde `chargeAttributes` pedir.
 *
 * ⚠️ Sub-rogação SAIU (AF-3671 step 2). Ela não é atributo do pagamento: é do TÍTULO, e ganha
 * fluxo próprio ligado a ele. Fonte e Aval também são da COBRANÇA e não do pagamento, mas
 * continuam aqui para a liquidação direta do settlement, que não passa por marcação. No billing
 * eles são informados na MARCAÇÃO, e por isso os dois chamadores de lá desligam o bloco.
 */
import {
    PaymentType, paymentTypeLabels, PaymentMethod, paymentMethodLabels,
    paymentSourceLabels, AssetDationItem,
} from '../../../constants/payment';
import ReceivingBankAccountSelect from './ReceivingBankAccountSelect';
import AssetDationForm, { emptyAsset } from './AssetDationForm';
import TextEdit from '../TextEdit';
import SelectTextField from '../SelectTextField';

export interface PaymentTypeValue {
    payment_type: string;
    payment_method: string;
    /** alias (ou uuid) da conta de recebimento */
    receiving_bank_account: string;
    debt_confession_contract: string;
    /**
     * O BEM dado em pagamento — UM, não uma lista.
     *
     * `null` enquanto o tipo de pagamento não for dação: o objeto nasce junto com a escolha, e
     * carregá-lo sempre faria o payload levar um bem vazio em pagamento financeiro.
     */
    asset_dation: AssetDationItem | null;
    payment_source: string;
    pending_guarantor_refund: boolean;
}

interface Props {
    value: PaymentTypeValue;
    onChange: (next: PaymentTypeValue) => void;
    disabled?: boolean;
    /** Meios oferecidos — o billing usa TRANSFER onde o settlement usa TED. */
    methods?: string[];
    labelClassName?: string;
    inputClassName?: string;
    /** prefixo dos ids dos checkboxes (duas instâncias na mesma página) */
    idPrefix?: string;
    /**
     * Tipos oferecidos. Default: todos. A liquidação de uma fatura não oferece DEBT_CONFESSION —
     * confissão de dívida é um instrumento que se registra no recebimento (step 1 do Registrar
     * Recebimento), não um jeito de baixar uma fatura específica.
     */
    types?: string[];
    /**
     * Mostrar Fonte do Pagamento e Aval a devolver.
     *
     * Default `true` porque a liquidação direta do settlement continua precisando deles — lá o
     * dinheiro baixa o título sem passar por marcação, então é ali que alguém informa. O billing
     * passa `false`: no fluxo de 3 passos quem informa é a MARCAÇÃO, e quem entrega o dinheiro
     * (passo 1) não sabe a qual cobrança ele pertence.
     */
    chargeAttributes?: boolean;
}

export const emptyPaymentTypeValue = (overrides: Partial<PaymentTypeValue> = {}): PaymentTypeValue => ({
    payment_type: PaymentType.FINANCIAL,
    payment_method: PaymentMethod.PIX,
    receiving_bank_account: '',
    debt_confession_contract: '',
    asset_dation: null,
    payment_source: 'SACADO',
    pending_guarantor_refund: false,
    ...overrides,
});

export const assetDationTotal = (asset: AssetDationItem | null | undefined): number =>
    Number(asset?.estimated_value || 0);

const DEFAULT_LABEL = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const DEFAULT_INPUT = 'w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed';

const PaymentTypeFields: React.FC<Props> = ({
    value, onChange, disabled, methods = [PaymentMethod.PIX, PaymentMethod.TED, PaymentMethod.BOLETO],
    labelClassName = DEFAULT_LABEL, inputClassName = DEFAULT_INPUT, idPrefix = 'pt', types,
    chargeAttributes = true,
}) => {
    // `types` é a lista PEDIDA (opcional); esta é a lista efetivamente renderizada
    const availableTypes = Object.entries(paymentTypeLabels)
        .filter(([key]) => !types || types.includes(key));
    const patch = (fields: Partial<PaymentTypeValue>) => onChange({ ...value, ...fields });

    return (
        <div className="space-y-3">
            <div>
                <label className={labelClassName}>Tipo de Pagamento</label>
                <div className={`grid gap-2 ${availableTypes.length >= 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                    {availableTypes.map(([key, text]) => (
                        <button key={key} type="button" disabled={disabled}
                            className={`px-3 py-2 text-sm rounded border transition-colors disabled:opacity-50 ${value.payment_type === key
                                ? 'bg-blue-500 text-white border-blue-500'
                                : 'bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-slate-600 hover:border-blue-300'
                                }`}
                            onClick={() => patch({ payment_type: key })}>
                            {text}
                        </button>
                    ))}
                </div>
            </div>

            {value.payment_type === PaymentType.FINANCIAL && (
                <div className="grid grid-cols-2 gap-3">
                    <SelectTextField
                        label="Meio de Pagamento"
                        items={methods.map(method => ({ value: method, label: paymentMethodLabels[method] || method }))}
                        value={value.payment_method}
                        disabled={disabled}
                        emptyLabel="Selecione"
                        onChange={e => patch({ payment_method: e.target.value })}
                    />
                    <ReceivingBankAccountSelect
                        label="Conta de Recebimento"
                        value={value.receiving_bank_account}
                        onChange={account => patch({ receiving_bank_account: account })} />
                </div>
            )}

            {value.payment_type === PaymentType.DEBT_CONFESSION && (
                <div>
                    <TextEdit
                        label="Contrato de Confissão de Dívida (opcional, Ex: CD0033)"
                        disabled={disabled}
                        value={value.debt_confession_contract}
                        onChange={e => patch({ debt_confession_contract: e.target.value })}
                        placeholder=" "
                    />
                    <p className="text-xs text-gray-400 mt-1">Vincule a um contrato CD existente ou deixe em branco para vincular depois.</p>
                </div>
            )}

            {value.payment_type === PaymentType.ASSET_DATION && (
                <AssetDationForm
                    idPrefix={`${idPrefix}-asset`}
                    value={value.asset_dation ?? emptyAsset()}
                    onChange={asset => patch({ asset_dation: asset })}
                />
            )}

            {chargeAttributes && (
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className={labelClassName} htmlFor={`${idPrefix}-payment-source`}>
                            Fonte do Pagamento
                        </label>
                        {/* `id`/`htmlFor` de verdade: o rótulo estava solto e o teste só o achava
                            por getByText, o que não prova associação para leitor de tela */}
                        <select id={`${idPrefix}-payment-source`} className={inputClassName} disabled={disabled}
                            value={value.payment_source} onChange={e => patch({ payment_source: e.target.value })}>
                            {Object.entries(paymentSourceLabels).map(([key, text]) => (
                                <option key={key} value={key}>{text}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex flex-col justify-end gap-2">
                        <div className="flex items-center gap-2">
                            <input type="checkbox" id={`${idPrefix}-guarantor-refund`} className="rounded" disabled={disabled}
                                checked={value.pending_guarantor_refund} onChange={e => patch({ pending_guarantor_refund: e.target.checked })} />
                            <label htmlFor={`${idPrefix}-guarantor-refund`} className="text-sm text-gray-700 dark:text-gray-300">Aval a devolver</label>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentTypeFields;
